export function formatOrdinalDay(day: number): string {
  if (day >= 11 && day <= 13) return `${day}th`;
  switch (day % 10) {
    case 1: return `${day}st`;
    case 2: return `${day}nd`;
    case 3: return `${day}rd`;
    default: return `${day}th`;
  }
}

export interface IsoWeek { year: number; week: number; }

export function getIsoWeek(date: Date): IsoWeek {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return { year: d.getUTCFullYear(), week };
}

export interface IsoWeekRange { start: Date; end: Date; }

export function getIsoWeekRange({ year, week }: IsoWeek): IsoWeekRange {
  const simple = new Date(Date.UTC(year, 0, 1 + (week - 1) * 7));
  const dayOfWeek = simple.getUTCDay();
  const isoWeekStart = simple;
  if (dayOfWeek <= 4) {
    isoWeekStart.setUTCDate(simple.getUTCDate() - dayOfWeek + 1);
  } else {
    isoWeekStart.setUTCDate(simple.getUTCDate() + 8 - dayOfWeek);
  }
  const isoWeekEnd = new Date(isoWeekStart);
  isoWeekEnd.setUTCDate(isoWeekStart.getUTCDate() + 4);
  return { start: isoWeekStart, end: isoWeekEnd };
}

export function getIsoWeeksForMonth(year: number, month: number): IsoWeek[] {
  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const weeks: IsoWeek[] = [];
  const seen = new Set<string>();
  const cursor = new Date(firstDayOfMonth);
  const day = cursor.getDay() || 7;
  cursor.setDate(cursor.getDate() - (day - 1));

  while (cursor <= lastDayOfMonth) {
    const iso = getIsoWeek(cursor);
    const key = `${iso.year}-${iso.week}`;
    if (!seen.has(key)) {
      seen.add(key);
      const range = getIsoWeekRange(iso);
      let countThisMonth = 0, countOtherMonth = 0;
      for (let d = new Date(range.start); d <= range.end; d.setDate(d.getDate() + 1)) {
        if (d.getFullYear() === year && d.getMonth() === month) countThisMonth++;
        else countOtherMonth++;
      }
      if (countThisMonth > countOtherMonth || (countThisMonth === countOtherMonth && range.end.getMonth() === month && range.end.getFullYear() === year)) {
        weeks.push(iso);
      }
    }
    cursor.setDate(cursor.getDate() + 7);
  }
  return weeks;
}

export interface MonthWeek { label: string; isoWeek: IsoWeek; range: IsoWeekRange; }

export function buildMonthWeeks(year: number, month: number): MonthWeek[] {
  return getIsoWeeksForMonth(year, month).map((iso, index) => ({
    label: `Week ${index + 1}`,
    isoWeek: iso,
    range: getIsoWeekRange(iso),
  }));
}

export interface MonthWindow { year: number; month: number; label: string; }

export function getMonthWindow(year: number, startMonth: number): MonthWindow[] {
  return Array.from({ length: 4 }).map((_, index) => {
    const d = new Date(year, startMonth + index, 1);
    return { year: d.getFullYear(), month: d.getMonth(), label: d.toLocaleString('default', { month: 'long' }) };
  });
}

export function isPastIsoWeek(isoWeek: IsoWeek): boolean {
  const { end } = getIsoWeekRange(isoWeek);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return end < today;
}

export function isPastMonth(year: number, month: number): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const weeks = buildMonthWeeks(year, month);
  if (!weeks.length) return true;
  return weeks.every((week) => week.range.end < today);
}

export function parseHoursMinutes(value: string): number {
  const [h, m] = value.split(':').map(Number);
  return h + m / 60;
}

export function resolveIsoWeek(assignedWeek: number, now: Date): IsoWeek {
  const currentIso = getIsoWeek(now);
  const year = assignedWeek < currentIso.week ? currentIso.year + 1 : currentIso.year;
  return { year, week: assignedWeek };
}

export function buildIsoWeekKey(isoWeek: IsoWeek): string {
  return `${isoWeek.year}-${isoWeek.week}`;
}
